
import { Component, Prop, Vue } from 'vue-property-decorator';
import * as Locale from '@/plugins/i18n';
import { reportingService } from '@/common/reporting';
import { GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import * as env from '@/common/environment';
import * as PleasedApi from '@/common/api/api.pleased';
import {
  MAX_DESCRIPTION_CHAR_NUM,
  MAX_GUEST_FIELDS_CHAR,
  MIN_DESCRIPTION_CHAR_NUM,
} from '@/common/config';
import GuestFormContent from '@/views/Chatroom/RuleEngineHelper/GuestForm/GuestFormContent.vue';
import LoggedInFormContent from '@/views/Chatroom/RuleEngineHelper/LoggedInForm/LoggedInFormContent.vue';
import { isEmailValid, isPhoneValid, replacePlaceholders } from '@/common/utils';
import { store } from '@/store';
import { FormField } from '@/common/types';
import Api from '@/common/api';
import { getRequestFields } from './RuleEngineHelper/LoggedInForm/LoggedInForm.utils';
import { getUserSelections, messageStore } from '@/common/bot/flow-frameworks/messages';
import { UserSelection } from '@/common/api/models.pleased';
import { BotConstants } from '@/common/bot/lib';
import { UserTransactionMessage } from '@/common/bot/types';
import { ReloadActiveConversationBus } from '@/common/eventBus';
import { UserState } from '@/store/user';
import { MatchedChatTrigger } from '@/common/bot/flow-frameworks/chat-trigger';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { analytics, CRITICAL_EVENTS, GENERAL_EVENTS } from '@/common/logs';
import { parseErrorMessage } from '@/common/error';

interface OpenRequestModalProps {
  trigger: MatchedChatTrigger;
  selectedOptions: {
    category?: string;
    section?: string;
    article?: string;
  };
  onEndChat?: () => void;
  onRestartChat?: () => void;
  onGoToMyRequest?: () => void;
}

@Component({
  components: {
  GuestFormContent,
  LoggedInFormContent,
  },
  directives: {
  trackClick,
  },
  mixins: [LogPageEventMixin],
  })
export default class OpenRequestModal extends Vue {
  @Prop() trigger!: MatchedChatTrigger;
  @Prop() selectedOptions!: OpenRequestModalProps['selectedOptions'];

  readonly Locale = Locale;
  readonly MAX_GUEST_FIELDS_CHAR = MAX_GUEST_FIELDS_CHAR;
  readonly MAX_DESCRIPTION_CHAR_NUM = MAX_DESCRIPTION_CHAR_NUM;

  enterDataStep = false;

  openRequestResponse: string | undefined = '';
  openRequestCreated: boolean = false;
  openRequestError: boolean = false;
  browseOtherTopics: boolean = false;
  creatingTicket = false;

  hasEmail = false;
  apiLoggedInFormFields: FormField[] = [];

  form = {
    name: '',
    phone: '',
    email: '',
    description: '',
    subject: '',
  };

  errors = {
    description: false,
    phone: false,
    email: false,
    name: false,
  };

  userSelections: UserSelection[] = [];

  get [PAGE_TRACKING] () {
    return {
      page: PAGE_EVENTS.MODAL_TRIGGER_OPEN_REQUEST,
      selection: this.trigger.chatTriggerId,
      metadata: {
        trigger: this.trigger,
      },
    };
  }

  get content () {
    if (this.openRequestError) {
      const middleErrorTextKey = this.isLoggedIn ? 'global.ticket.ticket_creation_failed.logged_in_suggestion' : 'global.ticket.ticket_creation_failed.guest_suggestion';
      const errorText = `${Locale.i18n.t(
        'global.ticket.ticket_creation_failed.start'
      )} ${Locale.i18n.t(middleErrorTextKey)} ${Locale.i18n.t(
        'global.ticket.ticket_creation_failed.end'
      )}`;

      return errorText;
    }

    return (
      this.openRequestResponse ||
      replacePlaceholders(this.trigger.message, {
        requester: {
          foreignId:
            (store?.state?.user as UserState)?.pleasedUser?.foreignId || '',
        },
      })
    );
  }

  get buttons () {
    const endChatButton = {
      name: Locale.i18n.t('global.end_chat') as string,
      trackKey: BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_END_CHAT,
      callback: this.endChat,
      class: ['primary'],
    };

    if (this.enterDataStep) {
      return [
        {
          name: Locale.i18n.t('global.back') as string,
          callback: this.onBackClick,
          trackKey: BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_BACK,
          class: [],
        },
        {
          name: Locale.i18n.t('global.proceed') as string,
          callback: this.onEnterDataStepFinish,
          trackKey: BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_PROCEED,
          class: ['primary full-width'],
        },
      ];
    }

    if (this.openRequestError) {
      return [endChatButton];
    }

    if (this.browseOtherTopics) {
      return [
        {
          name: Locale.i18n.t('chat.sys.navigation.restart_chat') as string,
          callback: this.restartChat,
          trackKey: BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_RESTART_CHAT,
          class: [],
        },
        endChatButton,
      ];
    }

    if (this.openRequestCreated) {
      return [
        {
          name: Locale.i18n.t(
            'page.resumable_chat.confirm.btn.go_to_my_request'
          ) as string,
          callback: this.goToMyRequest,
          trackKey: BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_GO_TO_MY_REQUESTS,
          class: ['primary full-width'],
        },
        {
          name: Locale.i18n.t('global.end_chat') as string,
          callback: this.endChat,
          trackKey: BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_END_CHAT,
          class: [],
        },
        {
          name: Locale.i18n.t('global.browse_other_topics') as string,
          callback: this.onBrowseOtherTopicsClick,
          class: [],
          trackKey: BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_BROWSE_OTHER_TOPICS,
        },
      ];
    }

    return [
      {
        name: Locale.i18n.t('global.open_request') as string,
        callback: this.onOpenRequestClick,
        trackKey: BTN_EVTS.MODAL_TRIGGER_OPEN_REQUEST_CLICKED,
        class: [],
      },
      endChatButton,
    ];
  }

  get ticketSubject () {
    const { matchSource } = this.trigger;

    const matchSourceMap = {
      CATEGORY: this.selectedOptions.category,
      SECTION: this.selectedOptions.section,
      ARTICLE: this.selectedOptions.article,
    };

    if (!matchSource || !matchSourceMap[matchSource]) {
      console.log('No match source found for', matchSource);
      return 'Request';
    }

    const textStart = `${Locale.i18n.t('chat.chat_trigger.open_request.request')}:`;

    if (matchSource === 'SECTION') {
      const category = this.selectedOptions.category;
      const section = matchSourceMap[matchSource];

      if (category) {
        return `${textStart} ${category} > ${section}`;
      } else {
        return `${textStart} ${section}`;
      }
    }

    if (matchSource === 'ARTICLE') {
      const category = this.selectedOptions.category;
      const article = matchSourceMap[matchSource];

      if (category) {
        return `${textStart} ${article} > ${category}`;
      } else {
        return `${textStart} ${article}`;
      }
    }

    return `${textStart} ${
      matchSourceMap[matchSource]
    }`;
  }

  get showTextarea () {
    const features = env.get('features');

    return (
      features?.chatTriggerOpenRequestDescriptionBox &&
      !this.openRequestCreated &&
      !this.openRequestError
    );
  }

  get ticketDescription () {
    const features = env.get('features');

    if (features?.chatTriggerOpenRequestDescriptionBox) {
      return this.form.description;
    }

    return this.ticketSubject;
  }

  get isLoggedIn () {
    return store.state.user.isLogin;
  }

  get staticLoggedInFormFields (): FormField[] {
    return [
      {
        id: -10000,
        displayName: Locale.i18n.t('global.description') as string,
        placeholder: Locale.i18n.t('global.describe') as string,
        name: 'description',
        required: true,
      },
      {
        id: -10001,
        displayName: Locale.i18n.t('global.subject') as string,
        placeholder: Locale.i18n.t('global.subject') as string,
        name: 'subject',
        required: false,
      },
    ];
  }

  get allFormFields () {
    return [...this.staticLoggedInFormFields, ...this.apiLoggedInFormFields];
  }

  get messageList () {
    return messageStore.get();
  }

  get issueMsg () {
    const message = this.messageList.find(m => m.id === BotConstants.IDS.USER_ISSUE_LIST) as UserTransactionMessage;
    if (message) {
      return message.issues.join(' / ');
    }
    return '';
  }

  created () {
    this.userSelections = getUserSelections(this.messageList);

    if (this.isLoggedIn) {
      const formFieldRequest = PleasedApi.getTicketFields().then(res =>
        res
          .map(f => ({ ...f, required: f.isMandatory, name: f.fieldName }))
          .filter(item => item.name !== 'email')
      );

      Promise.all([Api.getAccountInfo(), formFieldRequest]).then(
        ([res, formFields]) => {
          this.apiLoggedInFormFields = formFields;
          if (res.bizCode === 10000) {
            res.data.email &&
              store.commit('user/setUserPhoneOrEmailIfNotExists', {
                email: res.data.email,
              });
            res.data.phone &&
              store.commit('user/setUserPhoneOrEmailIfNotExists', {
                phone: res.data.phone,
              });
          }

          if (res.bizCode === 10000 && res.data.email) {
            this.form.email = res.data.email;
            this.hasEmail = true;
          }
        }
      );

      this.form.subject = this.ticketSubject;
    }
  }

  validate () {
    let result = true;

    if (this.isLoggedIn) {
      const isDescriptionRequired = !!this.allFormFields.find(
        item => item.name === 'description' && item.required === true
      );
      const isEmailRequired = !!this.allFormFields.find(
        item => item.name === 'email' && item.required === true
      );

      const textLength = this.form.description.length;

      if (
        isDescriptionRequired &&
        this.form.description !== undefined &&
        (textLength < MIN_DESCRIPTION_CHAR_NUM ||
          textLength > MAX_GUEST_FIELDS_CHAR.description)
      ) {
        this.errors.description = true;
        result = false;
      }
      if (isEmailRequired && !isEmailValid(this.form.email)) {
        this.errors.email = true;
        result = false;
      }
    } else {
      if (!isPhoneValid(this.form.phone)) {
        this.errors.phone = true;
        result = false;
      }
      if (!isEmailValid(this.form.email)) {
        this.errors.email = true;
        result = false;
      }

      const textLength = this.form.description.length;
      if (
        textLength < MIN_DESCRIPTION_CHAR_NUM ||
        textLength > MAX_GUEST_FIELDS_CHAR.description
      ) {
        this.errors.description = true;
        result = false;
      }

      if (!this.form.name.length) {
        this.errors.name = true;
        result = false;
      }
    }

    return result;
  }

  async openRequest () {
    if (this.creatingTicket) return;
    this.creatingTicket = true;
    const ticketFields = getRequestFields({
      form: this.form,
      allFields: this.allFormFields,
      issueMsg: this.issueMsg,
      userEmail: store.state.user.email,
      userPhone: store.state.user.phone,
    })
      .filter(f => f.id > 0)
      .map(f => ({ fieldId: f.id, fieldValue: f.value }));

    reportingService.openRequestClicked();
    this.$gtag.event(GA_EVENT_NAME_ENUM.START_OPEN_REQUEST_CREATION);

    const pleasedUser = store.state.user.pleasedUser;
    const payload = {
      chatTriggerId: this.trigger.chatTriggerId,
      subject: this.ticketSubject,
      description: this.ticketDescription,
      recipientAddress: this.form.email || pleasedUser.email,
      ticketFields,
      userSelections: this.userSelections,
      userLogs: reportingService.getLogs(),
      chatbotSessionId: analytics.sessionId,
    };

    try {
      const result = await PleasedApi.createTicket(payload);

      const responseText = result.chatTriggerResponse ||
        (Locale.i18n.t('global.ticket.ticket_created', {
          ticketId: result.id,
        }) as string);

      this.openRequestResponse = replacePlaceholders(responseText, {
        requester: {
          name: this.form.name,
          phone: this.form.phone,
          email: this.form.email,
          foreignId: '',
        },
        ticketId: result.id,
      }, {
        requester: {
          name: 'User',
          phone: '',
          email: '',
          foreignId: '',
        },
      });

      this.openRequestCreated = true;
      this.trigger.report.openRequest(Boolean(result.isCreated));
      ReloadActiveConversationBus.pub();
    } catch (error) {
      analytics.trackCriticalEvent({
        name: CRITICAL_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST_INITIATED_ERROR,
        metadata: {
          error: parseErrorMessage(error),
          payload,
        },
      });

      this.openRequestError = true;
      // On error we should not track this, we are saying that
      // the end user clicked on openRequest, rather than an error has occurred.
      // this.trigger.report.openRequest(false);
    }
    this.creatingTicket = false;
    this.enterDataStep = false;
  }

  async onEnterDataStepFinish () {
    const validation = this.validate();

    if (!validation) {
      return;
    }

    this.openRequest();

    if (!this.isLoggedIn) {
      const userData = await PleasedApi.getAnonymousToken();

      const config = {
        ...userData,
        isGuest: true,
        name: this.form.name || userData.name || '',
        email: this.form.email || userData.email || '',
        phone: this.form.phone || userData.phone || '',
      };

      console.log('onEnterDataStepFinish userData', userData);
      store.commit('user/updateGuestUser', config);
      store.commit('user/updatePleasedUser', config);
    }
  }

  onBackClick () {
    this.enterDataStep = false;
  }

  onOpenRequestClick () {
    this.enterDataStep = true;
  }

  onBrowseOtherTopicsClick () {
    this.$emit('restartChat');
  }

  restartChat () {
    this.$emit('restartChat');
  }

  goToMyRequest () {
    this.$emit('goToMyRequest');
  }

  endChat () {
    this.$emit('endChat');
  }
}
